import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api } from "@/lib/quotationApi";
import FloatingSaveBar from "@/components/FloatingSaveBar";
import PlanEditor from "@/components/admin/plans/PlanEditor";

// Stable client-side key for list items that lack a server-side id (e.g. milestones).
const makeKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `k_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);

const decoratePlans = (plans) =>
  (plans || []).map((p) => ({
    ...p,
    milestones: (p.milestones || []).map((m) =>
      m && m._key ? m : { ...m, _key: makeKey() },
    ),
  }));

// Returns an error message string or null when all plans are valid.
const validatePlans = (plans) => {
  for (const p of plans) {
    if (!(p.name || "").trim()) return "Every plan needs a name";
    if (!(p.milestones || []).length) return `${p.name}: add at least one milestone`;
    let sum = 0;
    for (const m of p.milestones) {
      if (!(m.label || "").trim()) return `${p.name}: milestone label required`;
      const pct = parseFloat(m.percent);
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        return `${p.name} — ${m.label}: percent must be 0–100`;
      }
      sum += pct;
    }
    if (sum > 100.01) {
      return `${p.name}: milestone percentages total ${sum.toFixed(1)}% (max 100)`;
    }
  }
  return null;
};

export default function PaymentPlansManager() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/payment-plans");
      // Decorate milestones with a stable client-side key so React reconciles
      // correctly across reorders / deletes (server doesn't provide one).
      setPlans(decoratePlans(r.data?.plans));
    } catch (e) {
      console.warn("Failed to load payment plans:", e);
      toast.error("Failed to load payment plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addPlan = () => {
    setPlans((prev) => [
      ...prev,
      {
        id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        name: `Plan ${prev.length + 1}`,
        description: "",
        milestones: [{ _key: makeKey(), label: "Advance on confirmation", percent: 50, note: "" }],
      },
    ]);
  };

  const removePlan = (idx) => {
    setPlans((prev) => prev.filter((_, i) => i !== idx));
  };

  const updatePlan = (idx, patch) => {
    setPlans((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const addMilestone = (pIdx) => {
    setPlans((prev) =>
      prev.map((p, i) =>
        i === pIdx
          ? {
              ...p,
              milestones: [
                ...(p.milestones || []),
                { _key: makeKey(), label: "Next milestone", percent: 0, note: "" },
              ],
            }
          : p,
      ),
    );
  };

  const removeMilestone = (pIdx, mIdx) => {
    setPlans((prev) =>
      prev.map((p, i) =>
        i === pIdx
          ? { ...p, milestones: p.milestones.filter((_, j) => j !== mIdx) }
          : p,
      ),
    );
  };

  const updateMilestone = (pIdx, mIdx, patch) => {
    setPlans((prev) =>
      prev.map((p, i) =>
        i === pIdx
          ? {
              ...p,
              milestones: p.milestones.map((m, j) =>
                j === mIdx ? { ...m, ...patch } : m,
              ),
            }
          : p,
      ),
    );
  };

  const save = async () => {
    const error = validatePlans(plans);
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        plans: plans.map((p) => ({
          id: p.id?.startsWith("tmp_") ? undefined : p.id,
          name: (p.name || "").trim(),
          description: (p.description || "").trim(),
          milestones: (p.milestones || []).map((m) => ({
            label: (m.label || "").trim(),
            percent: parseFloat(m.percent) || 0,
            note: (m.note || "").trim(),
          })),
        })),
      };
      const r = await api.put(`/settings/payment-plans`, payload);
      setPlans(decoratePlans(r.data?.plans));
      toast.success("Payment plans updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-white/40 text-xs">Loading payment plans…</div>;
  }

  return (
    <div
      className="mt-6 pt-6 border-t border-white/5 space-y-4"
      data-testid="payment-plans-manager"
    >
      <div>
        <div className="overline text-[#C5A880] mb-2">Payment plans</div>
        <p className="text-white/50 text-sm">
          Define one or more payment schedules. The sales person picks one per
          quotation in Step 5 (Tiers). Each plan stays optional.
        </p>
      </div>

      <div className="space-y-4">
        {plans.map((plan, pIdx) => (
          <PlanEditor
            key={plan.id || pIdx}
            plan={plan}
            pIdx={pIdx}
            onPatch={(patch) => updatePlan(pIdx, patch)}
            onRemove={() => removePlan(pIdx)}
            onAddMilestone={() => addMilestone(pIdx)}
            onRemoveMilestone={(mIdx) => removeMilestone(pIdx, mIdx)}
            onUpdateMilestone={(mIdx, patch) => updateMilestone(pIdx, mIdx, patch)}
          />
        ))}
        {plans.length === 0 && (
          <div className="rounded-xl border border-dashed border-white/10 p-5 text-center text-white/40 text-sm">
            No payment plans yet. Add one to offer structured payment terms on
            quotations.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-2">
        <button
          onClick={addPlan}
          className="btn-ghost flex items-center justify-center gap-2"
          data-testid="payment-plan-add-btn"
        >
          <Plus size={16} /> Add payment plan
        </button>
      </div>

      <FloatingSaveBar
        onSave={save}
        saving={saving}
        label="Save plans"
        savingLabel="Saving…"
        testId="payment-plans-save-btn"
      />
    </div>
  );
}
